import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token from localStorage on every request (fallback if cookies fail)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401 response, clear stored token
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401) {
      // Don't clear token for /auth/me (initial check) — only for admin calls
      const url = error?.config?.url || "";
      if (!url.includes("/auth/me")) {
        localStorage.removeItem("access_token");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Public endpoints
export const getTrips = (params) => api.get("/trips", { params });
export const getTripBySlug = (slug) => api.get(`/trips/${slug}`);
export const getDestinationInfo = (slug) => api.get(`/destinations/${slug}`);
export const getBlogPosts = (params) => api.get("/blog", { params });
export const getBlogBySlug = (slug) => api.get(`/blog/${slug}`);
export const getTestimonials = (params) => api.get("/testimonials", { params });
export const getFAQs = () => api.get("/faqs");
export const getMoments = (params) => api.get("/moments", { params });
export const submitInquiry = (data) => api.post("/inquiries", data).then(res => {
  if (window.fbq) window.fbq("track", "Lead", { content_name: data.trip_slug || "general_inquiry", content_category: "form_submit" });
  return res;
});

// Auth
export const login = (data) => api.post("/auth/login", data);
export const logout = () => api.post("/auth/logout");
export const getMe = () => api.get("/auth/me");

// Admin endpoints
export const getAdminStats = () => api.get("/admin/stats");
export const getAdminTrips = () => api.get("/admin/trips");
export const createTrip = (data) => api.post("/admin/trips", data);
export const updateTrip = (id, data) => api.put(`/admin/trips/${id}`, data);
export const deleteTrip = (id) => api.delete(`/admin/trips/${id}`);
export const bulkDeleteTrips = (ids) => api.post("/admin/trips/bulk-delete", { ids });
export const getAdminBlog = () => api.get("/admin/blog");
export const createBlog = (data) => api.post("/admin/blog", data);
export const updateBlog = (id, data) => api.put(`/admin/blog/${id}`, data);
export const deleteBlog = (id) => api.delete(`/admin/blog/${id}`);
export const getAdminInquiries = () => api.get("/admin/inquiries");
export const updateInquiryStatus = (id, status) => api.put(`/admin/inquiries/${id}/status`, { status });
export const deleteInquiry = (id) => api.delete(`/admin/inquiries/${id}`);
export const createTestimonial = (data) => api.post("/admin/testimonials", data);
export const updateTestimonial = (id, data) => api.put(`/admin/testimonials/${id}`, data);
export const deleteTestimonial = (id) => api.delete(`/admin/testimonials/${id}`);
export const createFAQ = (data) => api.post("/admin/faqs", data);
export const updateFAQ = (id, data) => api.put(`/admin/faqs/${id}`, data);
export const deleteFAQ = (id) => api.delete(`/admin/faqs/${id}`);

// Banners
export const getBanners = () => api.get("/banners");
export const getAdminBanners = () => api.get("/admin/banners");
export const createBanner = (data) => api.post("/admin/banners", data);
export const updateBanner = (id, data) => api.put(`/admin/banners/${id}`, data);
export const deleteBanner = (id) => api.delete(`/admin/banners/${id}`);

// Site Settings
export const getSettings = () => api.get("/settings");
export const updateSettings = (data) => api.put("/admin/settings", data);

// Moments (Captured Memories)
export const getAdminMoments = () => api.get("/admin/moments");
export const createMoment = (data) => api.post("/admin/moments", data);
export const updateMoment = (id, data) => api.put(`/admin/moments/${id}`, data);
export const deleteMoment = (id) => api.delete(`/admin/moments/${id}`);

export default api;
